import { animate, useReducedMotion } from "framer-motion";
import { useLayoutEffect, useRef } from "react";

import { EASE_OUT } from "./motionPresets";

const formatWithThousandsSeparator = (number) => Math.round(number).toLocaleString();

/**
 * A number that counts from its old value to its new one, e.g. a total going
 * from 22,000 to 33,000 when Meet & Assist is added. Shows the value as-is on
 * first render -- nothing counts up from zero when the page loads.
 *
 * <AnimatedNumber value={breakdown.totalPrice} />円
 *
 * The text is written straight into the span instead of through React state,
 * so a running count doesn't re-render the whole price card ~60 times a
 * second. That is also why React never renders children here: React would
 * otherwise keep its own text node and fight with the animation over it.
 */
const AnimatedNumber = ({
  value,
  format = formatWithThousandsSeparator,
  duration = 0.6,
  className,
}) => {
  const shouldReduce = useReducedMotion();
  const numberElementRef = useRef(null);
  // The figure currently on screen. When the value changes mid-count, the new
  // count starts from here rather than jumping to the old target first.
  const displayedValueRef = useRef(null);

  // Layout effect, so the first figure is written before the browser paints
  // -- the span is never seen empty.
  useLayoutEffect(() => {
    const numberElement = numberElementRef.current;
    if (!numberElement) return undefined;

    const showValue = (valueToShow) => {
      displayedValueRef.current = valueToShow;
      numberElement.textContent = format(valueToShow);
    };

    const displayedValue = displayedValueRef.current;
    const canCount =
      !shouldReduce &&
      Number.isFinite(displayedValue) &&
      Number.isFinite(value) &&
      displayedValue !== value;

    if (!canCount) {
      showValue(value);
      return undefined;
    }

    const countAnimation = animate(displayedValue, value, {
      duration,
      ease: EASE_OUT,
      onUpdate: showValue,
    });
    return () => countAnimation.stop();
    // `format` is usually an inline arrow; re-running on every render would
    // restart the count, so only a new value starts one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, shouldReduce, duration]);

  return <span ref={numberElementRef} className={className} />;
};

export default AnimatedNumber;
